import { useState } from "react";
import {
  Bell,
  Check,
  CircleCheck,
  Clock,
  Mail,
  TriangleAlert,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { Alert } from "@lytra/frontend-common/components/v2/ui/alert";
import { PersonAvatar } from "@/components/PersonAvatar";
import { Badge } from "@lytra/frontend-common/components/v2/ui/badge";
import { Button } from "@lytra/frontend-common/components/v2/ui/button";
import { Icon } from "@lytra/frontend-common/components/v2/ui/icon";
import { Throbber } from "@lytra/frontend-common/components/v2/ui/throbber";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@lytra/frontend-common/components/v2/ui/tooltip";
import {
  ForwardMailFailureReason,
  ForwardMailStatus,
  ForwardRecipientKind,
  ForwardRequestStatus,
} from "@lytra/frontend-common/__generated__/graphql";
import { cn } from "@/lib/utils";
import { deliveryProblemReason } from "./DeliveryProblemNotice";
import type { MentionableAgent } from "./NoteBody";
import type { TicketMessage } from "./noteThreads";
import {
  RECIPIENT_LIMIT,
  failedProblemsFor,
  isViewerRecipient,
  lytraRecipientName,
  unmatchedFailedProblems,
  visibleRecipients,
} from "./forwardRecipients";

/** What the entry reads of a send. The failure reason and the mailbox are required (null
 * when there is none), so a caller cannot leave them out and lose why a mail was not sent. */
export type ForwardEntryMessage = Pick<
  TicketMessage,
  | "id"
  | "forwardRecipients"
  | "forwardMailStatus"
  | "deliveryProblems"
  | "forwardMailFailureReason"
  | "emailMseMailboxUserPrincipalName"
> &
  Partial<Pick<TicketMessage, "forwardRequestStates">>;

/** The forwarded message as the "Gesendete Nachricht anzeigen" button names it. */
export interface ForwardQuoteSource {
  sender: string;
  /** The time, already formatted for display. */
  at: string;
}

interface FailureAlertProps {
  address: string;
  reason?: string | null;
}

/** The mail to one address did not arrive; the reason is the one the bounce reported (ADR-0057). */
function FailureAlert({ address, reason }: FailureAlertProps) {
  const { t } = useTranslation();
  return (
    <Alert tone="error" announce={false} className="mt-1">
      <p>{t("tickets.forward.entry.deliveryFailed", { address })}</p>
      {reason && <p className="text-ink-muted">{reason}</p>}
    </Alert>
  );
}

interface NotSentAlertProps {
  reason?: ForwardMailFailureReason | null;
  mailbox?: string | null;
}

/** The mail never left: one sentence for the whole send that says why. A bounce is not this. */
function NotSentAlert({ reason, mailbox }: NotSentAlertProps) {
  const { t } = useTranslation();
  let text: string;
  if (reason === ForwardMailFailureReason.MailboxDenied) {
    text = mailbox
      ? t("tickets.forward.entry.notSent.MAILBOX_DENIED", { mailbox })
      : t("tickets.forward.entry.notSent.MAILBOX_DENIED_UNNAMED");
  } else if (reason && reason !== ForwardMailFailureReason.Bounced) {
    text = t(`tickets.forward.entry.notSent.${reason}`);
  } else {
    text = t("tickets.forward.entry.notSent.UNKNOWN");
  }
  return (
    <Alert tone="error" announce={false} className="mt-1">
      <p>{text}</p>
    </Alert>
  );
}

const REQUEST_STATUS: Record<
  ForwardRequestStatus,
  { color: string; icon: LucideIcon }
> = {
  [ForwardRequestStatus.Waiting]: { color: "text-warning-strong", icon: Clock },
  [ForwardRequestStatus.Confirmed]: { color: "text-info-strong", icon: Check },
  [ForwardRequestStatus.Answered]: {
    color: "text-success-strong",
    icon: CircleCheck,
  },
  [ForwardRequestStatus.Done]: { color: "text-ink-muted", icon: CircleCheck },
};

/** What the rows read of a colleague: the name, and the address that is the row's second line. */
export interface ForwardEntryAgent extends MentionableAgent {
  emailAddress?: string | null;
}

interface ForwardEntryProps {
  message: ForwardEntryMessage;
  viewerId?: string;
  agentsById: ReadonlyMap<string, ForwardEntryAgent>;
}

/**
 * The "An" list of a share to colleagues: one row per recipient with avatar, name, channel
 * badge and a second line with the address and where the recipient stands. A colleague in
 * lytra shows the request's status (waiting, confirmed, answered, done), which is what makes
 * an internal share a request and not a note. A mail recipient shows what became of the mail,
 * and "Extern" when the address was foreign at send time, because only the mail leg leaves
 * the company. Delivery is read only from the send's stored state (PENDING, SENT, FAILED), so
 * nothing claims "Gesendet" before Mercury reported it and a send nobody reported on within a
 * day shows FAILED instead of "Wird gesendet …" forever. A FAILED mail that bounced says so in
 * each bounced row; one that never left says why once, from its stored reason. Everything stored (labels, addresses)
 * is rendered as React text, never HTML (T-03-36).
 */
export function ForwardEntry({
  message,
  viewerId,
  agentsById,
}: ForwardEntryProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const recipients = message.forwardRecipients;
  const mailRecipients = recipients.filter(
    (r) => r.kind !== ForwardRecipientKind.Lytra,
  );
  const lytraRecipients = recipients.filter(
    (r) => r.kind === ForwardRecipientKind.Lytra,
  );
  const stateOf = new Map(
    (message.forwardRequestStates ?? []).map((s) => [
      String(s.userId).toLowerCase(),
      s.status,
    ]),
  );
  const shown = visibleRecipients(mailRecipients, viewerId, expanded);
  const hidden = mailRecipients.length - RECIPIENT_LIMIT;
  const status = message.forwardMailStatus;
  const stray = unmatchedFailedProblems(message.deliveryProblems, recipients);
  // A FAILED send either went out and bounced (each bounced address says so in its row) or
  // never left, which one sentence for the whole send explains.
  const bounced =
    status === ForwardMailStatus.Failed &&
    message.forwardMailFailureReason === ForwardMailFailureReason.Bounced;
  const anyBounce =
    stray.length > 0 ||
    mailRecipients.some(
      (r) =>
        failedProblemsFor(message.deliveryProblems, r.address ?? r.label)
          .length > 0,
    );
  const notSent = status === ForwardMailStatus.Failed && !bounced && !anyBounce;

  return (
    <TooltipProvider>
      <div className="flex min-w-0 flex-col gap-1 border-t border-dashed border-primary-line pt-3">
        <p className="text-micro font-semibold tracking-wide text-ink-muted uppercase">
          {t("tickets.forward.entry.to")}
        </p>
        <ul
          aria-label={t("tickets.forward.entry.recipients")}
          className="flex min-w-0 flex-col gap-1.5"
        >
          {lytraRecipients.map((recipient) => {
            const name = lytraRecipientName(
              recipient,
              agentsById,
              t("tickets.notes.unknownAuthor"),
            );
            const isViewer = isViewerRecipient(recipient, viewerId);
            const state = stateOf.get(String(recipient.userId).toLowerCase());
            const address = recipient.userId
              ? agentsById.get(String(recipient.userId))?.emailAddress
              : null;
            const look = state ? REQUEST_STATUS[state] : null;
            return (
              <li
                key={String(recipient.userId ?? name)}
                className="flex min-w-0 flex-col"
              >
                <div className="flex min-h-6 min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
                  <PersonAvatar
                    personId={recipient.userId}
                    size="xs"
                    name={name}
                  />
                  <span
                    className={cn(
                      "min-w-0 max-w-full truncate text-body font-medium",
                      isViewer ? "text-primary-accent" : "text-ink",
                    )}
                  >
                    {name}
                    {isViewer && ` ${t("tickets.forward.entry.you")}`}
                  </span>
                  <Badge tone="info" size="xs" icon={Bell}>
                    {t("tickets.forward.recipients.channelLytra")}
                  </Badge>
                </div>
                {(Boolean(address) || look !== null) && (
                  <div className="flex min-w-0 items-center gap-2 pl-7 text-caption text-ink-muted">
                    {address && (
                      <span className="min-w-0 truncate">{address}</span>
                    )}
                    {state && look && (
                      <span
                        className={cn(
                          "ml-auto flex shrink-0 items-center gap-1",
                          look.color,
                        )}
                      >
                        <Icon icon={look.icon} size={12} />
                        {t(`tickets.forward.entry.status.${state}`)}
                      </span>
                    )}
                  </div>
                )}
              </li>
            );
          })}
          {shown.map((recipient) => {
            const address = recipient.address ?? recipient.label;
            const storedName = recipient.label.trim();
            const name =
              storedName && storedName.toLowerCase() !== address.toLowerCase()
                ? storedName
                : address;
            const failures = failedProblemsFor(
              message.deliveryProblems,
              address,
            );
            const failed = bounced || failures.length > 0;
            return (
              <li key={address} className="flex min-w-0 flex-col">
                {/* Wraps: in a 320-360px panel name, badges and the status do not fit one line. */}
                <div className="flex min-h-6 min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
                  <PersonAvatar size="xs" name={name} />
                  <span className="min-w-0 max-w-full truncate text-body font-medium text-ink">
                    {name}
                  </span>
                  <Badge tone="neutral" size="xs" icon={Mail}>
                    {t("tickets.forward.recipients.channelMail")}
                  </Badge>
                  {recipient.isExternal && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge
                          tone="warning"
                          size="xs"
                          icon={TriangleAlert}
                          tabIndex={0}
                        >
                          {t("tickets.forward.entry.extern")}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent>
                        {t("tickets.forward.warning.foreignPast")}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
                <div className="flex min-w-0 flex-wrap items-center gap-x-2 pl-7 text-caption text-ink-muted">
                  {name !== address && (
                    <span className="min-w-0 max-w-full truncate">
                      {address}
                    </span>
                  )}
                  {!failed && status === ForwardMailStatus.Pending && (
                    <span className="ml-auto flex shrink-0 items-center gap-1">
                      <Throbber size={12} decorative />
                      {t("tickets.forward.entry.sending")}
                    </span>
                  )}
                  {!failed && status === ForwardMailStatus.Sent && (
                    <span className="ml-auto flex shrink-0 items-center gap-1">
                      <Icon icon={Check} size={12} />
                      {t("tickets.forward.entry.sent")}
                    </span>
                  )}
                </div>
                {failed && (
                  <FailureAlert
                    address={address}
                    reason={
                      failures.length > 0
                        ? deliveryProblemReason(failures[0])
                        : null
                    }
                  />
                )}
              </li>
            );
          })}
        </ul>
        {hidden > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="self-start"
            aria-expanded={expanded}
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded
              ? t("tickets.forward.entry.fewer")
              : t("tickets.forward.entry.more", { count: hidden })}
          </Button>
        )}
        {notSent && (
          <NotSentAlert
            reason={message.forwardMailFailureReason}
            mailbox={message.emailMseMailboxUserPrincipalName}
          />
        )}
        {stray.map((problem) => (
          <FailureAlert
            key={problem.recipient}
            address={problem.recipient}
            reason={deliveryProblemReason(problem)}
          />
        ))}
      </div>
    </TooltipProvider>
  );
}
